import { useCarePlusPrice } from "../useCarePlusPrice";
import { Translate } from "../../ChoosePlanCarePlus/planContent";
import { CarePlusPlanDuration } from "../../ChoosePlanCarePlus/types";
import { CarePlusMedicationLine } from "../../ChoosePlanCarePlus/useChoosePlanCarePlus";
import OptionRadio from "./OptionRadio";
import FeatureRow from "./FeatureRow";
import { hasPlanDiscount } from "../pricing";

// the single "Monthly" Care+ card (HL-12001, Figma 6340:25350). The headline is medication +
// monthly fee stack, struck through over what the plan bonus leaves. Below it three check rows:
// what is included, the fee line, cancel anytime. The fee line and the footnote flip on the offer:
// a first_order bonus (the pill's sale) quotes the first month and drops the footnote;
// an all_orders bonus (injections) or none (other meds) quotes the recurring fee.

interface Props {
  t: Translate;
  duration: CarePlusPlanDuration | null;
  medication: CarePlusMedicationLine | null;
  isSelected: boolean;
  onSelect: () => void;
}

const MonthlyPlanCard = ({ t, duration, medication, isSelected, onSelect }: Props) => {
  const { formatPrice, formatFreePrice } = useCarePlusPrice();
  const bonus = duration?.bonus ?? null;
  const totalFee = duration?.total_fee ?? 0;
  const chargedFee = duration?.charged_fee ?? 0;
  const hasDiscount = hasPlanDiscount(duration);
  const isFirstOrderOnly = bonus?.applies_to === "first_order";

  const medicationPrice = medication?.official_price ?? medication?.item_price ?? 0;
  // The fee stack alone would headline "0 €" for a month that is not free, so the medication is
  // priced in. False only when nothing is picked yet (fee-stack-only fallback).
  const includeMedication = medicationPrice > 0;

  // A first_order offer (the pill's September sale) makes the headline a FIRST-MONTH price: "/mo"
  // is dropped, the fee row says "Erster Monat: Service …", and there is no footnote. An
  // all_orders offer (injections' shipping discount) or no offer is the same every month, so the
  // fee row quotes the recurring total (6252:23176). Driven by the offer on the plan row, not by
  // the product, so it flips by itself when the sale ends.
  const isFirstMonthPrice = isFirstOrderOnly && hasDiscount;

  const headerTotal = includeMedication ? medicationPrice + totalFee : totalFee;
  const headerCharged = includeMedication ? medicationPrice + chargedFee : chargedFee;

  const chargedFeeLabel = chargedFee === 0 ? t("free_lower", "free") : formatPrice(chargedFee);

  return (
    <div
      id="wl3_plan_monthly"
      data-testid="care-plus-v3-monthly-card"
      onClick={onSelect}
      className={`flex w-full cursor-pointer flex-col gap-[20px] rounded-[16px] border border-solid bg-white py-[12px] pl-[12px] pr-[14px] ${
        isSelected ? "border-[#f59603]" : "border-[#dee3ed]"
      }`}
    >
      {/* Header: radio + label + cadence, price stack on the right */}
      <div className="flex w-full items-start gap-[10px]">
        <div className="flex min-w-0 flex-1 items-start gap-[8px]">
          <OptionRadio selected={isSelected} />
          <div className="flex min-w-0 flex-1 flex-col gap-[2px]">
            <p className="text-[14px] font-medium leading-[21px] text-[#08080a]">{t("plan_monthly", "Monthly")}</p>
            <p className="text-[12px] leading-[18px] text-[#8a9099]">
              {t("medication_services_monthly", "Medication + services monthly")}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-[2px]">
          {hasDiscount && (
            <span
              data-testid="care-plus-v3-monthly-original-price"
              className="rounded-[4px] bg-[#ebf0f7] px-[4px] text-[12px] font-medium leading-[18px] text-[#08080a] line-through"
            >
              {formatPrice(headerTotal)}
            </span>
          )}
          <span className="flex items-baseline whitespace-nowrap">
            <span
              data-testid="care-plus-v3-monthly-charged-price"
              className={`text-[16px] font-semibold leading-[24px] ${hasDiscount ? "text-[var(--text-success)]" : "text-[#08080a]"}`}
            >
              {headerCharged === 0 ? formatFreePrice() : formatPrice(headerCharged)}
            </span>
          </span>
        </div>
      </div>

      {/* Feature rows */}
      <div className="flex w-full flex-col gap-[12px] pl-[4px]">
        <FeatureRow>{t("feat_medication_care_delivery", "Medication, medical care & delivery")}</FeatureRow>
        {/* Fee row only with an offer: first_order → "Erster Monat: Service kostenlos" (6340:25350);
            all_orders → recurring total struck through (6252:23176). No offer → no fee row,
            "Verlängert sich automatisch" takes its place (7212:30106). */}
        {hasDiscount && (
          <FeatureRow testId="care-plus-v3-monthly-services">
            {isFirstMonthPrice ? t("first_month_service", "First month: service") : t("total_fees_per_month", "Total fees per month:")}{" "}
            {!(isFirstMonthPrice && chargedFee === 0) && <span className="line-through">{formatPrice(totalFee)}</span>}{" "}
            <span className="font-semibold text-[var(--text-success)]">{chargedFeeLabel}</span>
          </FeatureRow>
        )}
        <FeatureRow>{t("feat_cancel_anytime", "Cancel anytime")}</FeatureRow>
        {!hasDiscount && <FeatureRow>{t("monthly_footnote", "Renews automatically")}</FeatureRow>}
      </div>

      {/* Footnote: the renewal caption. Under a first-order offer there is none (HL-12357 removed the
          "From the 2nd month" line). (HL-12294: the refund info tooltip next to it was removed.) */}
      {!isFirstMonthPrice && (
        <div className="flex w-full items-center gap-[8px] text-[12px] leading-[16px] text-[#99a3b2]">
          <p data-testid="care-plus-v3-monthly-footnote" className="min-w-0 flex-1">
            {t("monthly_footnote", "Renews automatically")}
          </p>
        </div>
      )}
    </div>
  );
};

export default MonthlyPlanCard;
